import React, { useState } from 'react'
import { Eye, EyeOff, AlertCircle, MonitorPlay, Mic, ListChecks, Star } from 'lucide-react'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { signIn } from 'supertokens-web-js/recipe/emailpassword'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../context/UserContext'
import logoImg from '../assets/AurilearnLogo.png'

const features = [
  {
    icon: MonitorPlay,
    title: 'Sees What You See',
    desc: 'Your lab assistant watches your screen in real time and adapts its guidance as you work.'
  },
  {
    icon: Mic,
    title: 'Talk As You Work',
    desc: 'Hold to talk and ask questions naturally — no breaking flow to type out what you need.'
  },
  {
    icon: ListChecks,
    title: 'Guided, Verified Practice',
    desc: 'Step through hands-on tasks with the assistant verifying your progress along the way.'
  }
]

export const LoginPage = (): React.JSX.Element => {
  const navigate = useNavigate()
  const { login } = useUser()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const response = await signIn({
        formFields: [
          { id: 'email', value: email },
          { id: 'password', value: password }
        ]
      })

      if (response.status === 'WRONG_CREDENTIALS_ERROR') {
        setError('Invalid email or password')
      } else if (response.status === 'SIGN_IN_NOT_ALLOWED') {
        setError('Sign in not allowed')
      } else if (response.status === 'OK') {
        await login()
        navigate('/dashboard')
      } else {
        setError('Login failed. Please try again.')
      }
    } catch (err) {
      setError(`Login failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen w-screen flex overflow-hidden">
      {/* ── LEFT PANEL ── */}
      <div className="hidden lg:flex lg:w-[52%] bg-[#1C1100] flex-col relative overflow-hidden">
        {/* Ambient blobs */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] bg-[#B8860B]/10 rounded-full" />
          <div className="absolute -bottom-24 -right-20 w-96 h-96 bg-[#B8860B]/8 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-[#775A00]/6 rounded-full" />
        </div>

        <div className="relative z-10 flex flex-col h-full px-14 py-10">
          {/* Hero copy */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 bg-[#B8860B]/20 border border-[#B8860B]/25 rounded-full px-4 py-1.5 mb-7 w-fit">
              <Star className="w-3.5 h-3.5 text-[#B8860B] fill-[#B8860B]" />
              <span className="text-[#B8860B] text-xs font-semibold tracking-wide uppercase">
                Hands-On AI Labs
              </span>
            </div>

            <h1 className="text-5xl xl:text-6xl font-bold text-white leading-[1.1] mb-5">
              Learn by doing,
              <br />
              <span className="text-[#B8860B]">with a co-pilot.</span>
            </h1>

            <p className="text-white/50 text-base leading-relaxed max-w-sm mb-10">
              A live lab assistant that sees your screen, listens to your questions, and walks you
              through real exercises — one task at a time.
            </p>

            {/* Feature list */}
            <div className="space-y-3">
              {features.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-white/[0.04] hover:bg-white/[0.07] transition rounded-2xl p-4 border border-white/[0.06]"
                >
                  <div className="w-9 h-9 rounded-xl bg-[#B8860B]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="w-[18px] h-[18px] text-[#B8860B]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold leading-snug">{title}</p>
                    <p className="text-white/40 text-xs mt-0.5 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quote footer */}
          <div className="border-t border-white/[0.08] pt-6">
            <p className="text-white/30 text-xs italic leading-relaxed">
              &ldquo;Tell me and I forget. Teach me and I remember. Involve me and I learn.&rdquo;
            </p>
            <p className="text-white/20 text-[11px] mt-1.5">— Benjamin Franklin</p>
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="w-full lg:w-[48%] bg-[#FDFAF5] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 sm:px-14 lg:px-16 xl:px-20 py-10">
          <div className="w-full max-w-[400px]">
            {/* Brand + Heading */}
            <div className="flex flex-col items-center text-center mb-10">
              <img
                src={logoImg}
                alt="Aurilearn"
                className="w-48 h-48 object-contain rounded-2xl"
              />
              <span className="text-[#775A00] text-5xl font-bold tracking-tight mt-1">
                Aurilearn
              </span>
              <h2 className="text-3xl font-bold text-[#1a1810] leading-tight mt-6">
                Welcome back
              </h2>
              <p className="mt-2 text-sm text-[#775A00]/55">
                Sign in to continue your learning journey
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              {/* EMAIL */}
              <div>
                <label className="block text-sm font-semibold text-[#2e2a22] mb-1.5">
                  Email address
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="h-12 px-4 bg-white border-[#775A00]/20 text-[#1a1810] rounded-xl w-full placeholder:text-gray-300 placeholder:italic placeholder:font-light shadow-sm focus-visible:ring-[#B8860B]/20 focus-visible:border-[#B8860B]"
                  style={{
                    WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
                    WebkitTextFillColor: '#1a1810'
                  }}
                />
              </div>

              {/* PASSWORD */}
              <div>
                <label className="block text-sm font-semibold text-[#2e2a22] mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSubmit(e)
                    }}
                    className="h-12 pl-4 pr-12 bg-white border-[#775A00]/20 text-[#1a1810] rounded-xl w-full placeholder:text-gray-300 placeholder:italic placeholder:font-light shadow-sm focus-visible:ring-[#B8860B]/20 focus-visible:border-[#B8860B]"
                    style={{
                      WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
                      WebkitTextFillColor: '#1a1810'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute top-1/2 -translate-y-1/2 right-4 text-[#775A00]/40 hover:text-[#775A00] transition"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* ERROR */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* SUBMIT */}
              <Button
                type="submit"
                disabled={isLoading}
                className="h-12 text-white bg-[#775A00] hover:bg-[#5E4600] rounded-xl text-sm font-semibold mt-1 transition shadow-md shadow-[#775A00]/25 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Signing in…</span>
                  </div>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
